'use client';

import { H4 } from '@/lib/StyledComponents/Typography';
import { type ContactCardBlockData } from '@/schema/cms/blocks/ContactCardBlockSchema';
import styled from 'styled-components';
import { type PartnerWidgetCardsBlockData } from '@/schema/cms/blocks/PartnerWidgetCardsBlockSchema';
import { type QuoteCardBlockData } from '@/schema/cms/blocks/QuoteCardBlockSchema';
import { ContactCard } from '@/app/(components)/CmsBlocks/ContactCard';
import { QuoteCard } from '@/app/(components)/CmsBlocks/QuoteCard';
import type { PartnersCardBlockData } from '@/schema/cms/blocks/PartnersCardBlock.schema';
import { mq } from '@/lib/StyledComponents/mq';
import type { SructuredTextCardBlockData } from '@/schema/cms/blocks/StructuredTextCardBlockSchema';
import { PartnersCard } from './PartnersCard';
import { CardWithStructuredText } from './CardWithStructuredText';
import type { StructuredTextGraphQlResponse } from 'react-datocms';

type ContainerType = {
  $offset: boolean;
  $isPartnersCard: boolean;
  $cardType:
    | 'ContactCardRecord'
    | 'QuoteCardRecord'
    | 'PartnersCardRecord'
    | 'PartnerWidgetCardRecord'
    | 'StructuredTextCardRecord';
};

const Container = styled.div<ContainerType>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.dark1};
  padding: ${({ $isPartnersCard }) =>
    $isPartnersCard ? '0 0 5rem 0' : '3.75rem 0'};

  ${({ $offset, theme }) =>
    $offset &&
    `
      > :before {
        width: 100%;
        content: '';
        position: absolute;
        left: 0;
        background-color: ${theme.colors.background.light};

      }
    `}

  ${({ $cardType }) => {
    switch ($cardType) {
      case 'ContactCardRecord':
        return `
          > :before {
              height: 80.5%;
              top: 20%;
              
            ${mq('laptop')}{
              height: 55%;
              top: 45%;
            }
            
          }
    `;
      case 'PartnersCardRecord':
      case 'StructuredTextCardRecord':
        return `
          > :before {
              height: 70.5%;
              top: 30%;
              
            ${mq('laptop')}{
              height: 65%;
              top: 35%;
            }
            
          }
    `;
      case 'QuoteCardRecord':
        return `
          > :before {
              height: 75.5%;
              top: 25%;
              
            ${mq('laptop')}{
              height: 65%;
              top: 35%;
            }
            
          }
    `;
    }
  }}
`;

const StyledH4 = styled(H4)`
  font-size: 1rem;
  padding: 0 2.5rem;
  text-align: center;

  ${mq(null, 'laptop')} {
    font-size: 0.875rem;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  width: 100%;
`;

type DynamicCardsSectionProps = {
  cards: (
    | ContactCardBlockData
    | QuoteCardBlockData
    | PartnersCardBlockData
    | PartnerWidgetCardsBlockData
    | SructuredTextCardBlockData
  )[];
  title?: string | null;
  offset: boolean;
};

export const DynamicCardsSection = ({
  title,
  cards,
  offset,
}: DynamicCardsSectionProps) => {
  if (cards.length === 0 && !title) return null;

  const isPartnersCard = cards[0].__typename === 'PartnersCardRecord';

  return (
    <Container
      $offset={offset}
      $isPartnersCard={isPartnersCard}
      $cardType={cards[0].__typename}
    >
      {title && (
        <StyledH4 $textColor="primary" $uppercase>
          {title}
        </StyledH4>
      )}

      {cards.length > 0 && (
        <CardsContainer>
          {cards.map(card => {
            switch (card.__typename) {
              case 'ContactCardRecord':
                return (
                  <ContactCard
                    key={card.id}
                    image={card.image}
                    jobTitle={card.jobTitle}
                    name={card.name}
                    phoneNumber={card.phoneNumber}
                    emailAddress={card.emailAddress}
                  />
                );
              case 'QuoteCardRecord':
                return (
                  <QuoteCard
                    key={card.id}
                    image={card.image}
                    quote={card.quote}
                    quoteBy={card.quoteBy}
                  />
                );
              case 'PartnersCardRecord':
                return <PartnersCard key={card.id} partners={card.logos} />;
              case 'StructuredTextCardRecord':
                return (
                  <CardWithStructuredText
                    textAlign={card.textAlign}
                    structureText={card.body as StructuredTextGraphQlResponse}
                  ></CardWithStructuredText>
                );
            }
          })}
        </CardsContainer>
      )}
    </Container>
  );
};
